import React from 'react';
import PropTypes from 'prop-types';
import { AdvancedSearch } from 'smartsuite-advanced-search-app';
import { useTranslation } from 'react-i18next';
import useFavorite from '../../hooks/useFavorite';
import getUri, { getNonApiUri, getPageHelpUri } from '../../utils/getUri';
import { getHelpPageUrl } from '../../utils/apiUtils';
import advancedSearchRoutes from './advancedSearchRoutes';
import resultContentFormatter from './resultContentFormatter';
import AdvancedSearchProvider from './AdvancedSearchContext';
import '../../i18n';

function getApiConstants(helpPageUrl) {
    return {
        getSearchUri: () => getUri('AdvancedSearchData/Search'),
        getDownloadUri: () => getUri('AdvancedSearchData/Download'),
        getEmailUri: () => getUri('AdvancedSearchData/Email'),
        getShareableUri: (externalLink) =>
            `${window.location.origin}${getNonApiUri(
                `apps/AdvancedSearch/${externalLink}`
            )}`,
        getPageConfigurationUri: (pageKey) =>
            getUri(
                `AdvancedSearchConfiguration/GetPageConfiguration?pageKey=${pageKey}`
            ),
        getRememberedSettingsUriByParameterId: (pageKey) =>
            getUri(
                `EnterpriseUserSettings/GetUserSetting?parameterId=${pageKey}`
            ),
        getPersistRememberedSettingsUri: () =>
            getUri('EnterpriseUserSettings/SaveUserSetting'),
        saveAdvancedSearch: () =>
            getUri('AdvancedSearchConfiguration/SaveAdvancedSearch'),
        getSavedAdvancedSearches: (pageKey) =>
            getUri(
                `AdvancedSearchConfiguration/GetAdvancedSearchSavedSearches?parameterId=${pageKey}`
            ),
        deleteAdvancedSearch: (pageKey) =>
            getUri(
                `AdvancedSearchConfiguration/RemoveSavedAdvancedSearch?parameterId=${pageKey}`
            ),
        saveSharedUrl: () => getUri('SharedUrl/SaveSharedUrl'),
        getSharedUrl: () => getUri('SharedUrl/GetSharedUrlById'),
        pageHelpUri: (onlineHelpId) =>
            getPageHelpUri({ helpPageUrl, onlineHelpId }),
        getAuditLogUri: () => getUri('AuditLogApi'),
        getSystemManifestUri: () =>
            getUri('SystemManifestApi/GetSystemManifest'),
    };
}

export default function AppWrapper(props) {
    const advancedSearchTranslation = useTranslation('advancedSearch');
    const appTranslation = useTranslation('app');
    const ssiTranslation = useTranslation('ssi');
    const serverResponseErrorMessagesTranslation = useTranslation(
        'serverResponseErrorMessages'
    );
    console.log('trying to advanced search');
    const pageTitles = advancedSearchTranslation.t('pageTitles', {
        returnObjects: true,
    });
    const labels = advancedSearchTranslation.t('labels', {
        returnObjects: true,
    });
    const pageSpecificLabels = advancedSearchTranslation.t(
        'pageSpecificLabels',
        {
            returnObjects: true,
        }
    );
    const dateTimeRangePicker = appTranslation.t('dateTimeRangePicker', {
        returnObjects: true,
    });
    const dataGridLabels = appTranslation.t('dataGrid', {
        returnObjects: true,
    });
    const ssiLabels = ssiTranslation.t('ssiLabels', {
        returnObjects: true,
    });
    const errorMessagesByStatusCode = serverResponseErrorMessagesTranslation.t(
        'ErrorMessages',
        {
            returnObjects: true,
        }
    );

    const favoriteUtilities = useFavorite();
    const { helpPageUrl } = getHelpPageUrl();

    // TODO: Remove apps/framework/ portion once we use our own configs separate from Operational
    return (
        <AdvancedSearchProvider apiConstants={getApiConstants(helpPageUrl)}>
            <AdvancedSearch
                apiConstants={getApiConstants(helpPageUrl)}
                appInsightsFunctions={props.appInsightsFunctions}
                advancedSearchRoutes={advancedSearchRoutes}
                basename={'AdvancedSearch'}
                customContentRenderer={resultContentFormatter}
                favoriteUtilities={favoriteUtilities}
                labels={{
                    pageTitles,
                    dateTimeRangePicker,
                    labels,
                    errorMessagesByStatusCode,
                    pageSpecificLabels,
                    ssiLabels,
                    dataGridLabels,
                }}
                path={props.path}
            />
        </AdvancedSearchProvider>
    );
}

AppWrapper.propTypes = {
    appInsightsFunctions: PropTypes.instanceOf(Object).isRequired,
    path: PropTypes.string.isRequired,
};
